/**
 * QZ Tray abstraction layer.
 * Wraps the qz-tray npm package for connecting, listing printers,
 * and sending print jobs (raw ESC/POS or PDF).
 *
 * Authentication: Self-signed certificate with client-side SHA-512 signing
 * via Web Crypto API. The matching override.crt must be placed in the
 * QZ Tray installation directory on each workstation.
 */

import qz from 'qz-tray';

// ── Self-signed certificate & private key for QZ Tray signing ────────────
// The certificate (public) is fetched from the web server.
// The private key is embedded here for client-side signing. This is acceptable
// for an internal ERP where the key does not grant any external access --
// it only authorizes print commands to the local QZ Tray instance.

const QZ_PRIVATE_KEY_PEM =
  '-----BEGIN PRIVATE KEY-----\n' +
  'MIIEvAIBADANBgkqhkiG9w0BAQEFAASCBKYwggSiAgEAAoIBAQC16ULc8eCvQsLC\n' +
  'zjI61DCxftCI2mRBmpuVPmxAxgHsaKFWy/7pvEtU1RP95ka7a/I50j3MyZbt30hB\n' +
  'I4gSNazQGEkCwgykmUZ9tGppLqYIaucqdaXG77IVmgak1CUUDpniwDQCSSgZrICa\n' +
  'Maw9CO8lh3LCt6Lhhd8Eoc5jNFG7p4fo+mjvLoiQo2g/QoD7tvTrEFiLDqAi49E4\n' +
  'ojRa8ViJr3yJuA7HYaPcuywORpsd34WY+Ketu/Ev1DYyCjKMJFKdDTqqFGDSKEPw\n' +
  'zySd4IEO9RpaCoyVv7KZJQDx1KXnUToARxvMWMJ5v8KMoCm3oI5v0xrfw8uYFLhr\n' +
  'LDFOnY05AgMBAAECggEAOFQmIa9+URbdox/hWukYuOWZYj8p+0yKL8EYGUiXEOLn\n' +
  'Tq61XSAyvW6LlbvoHCIVbAUnoNGtfDP/Njhv6p+XRwq2FPYMny1dMBCdQLuyqvmb\n' +
  'YkDy1/KiFOFQfFq6robE7PtyP2Dy21xYqCaIfI/XKapTBWuCss3W3KLT+1EYnAAC\n' +
  '1hAxNtaDItdX4usC3sJ/QuKLiVIsssfsr5NEKlWmNy1q4o0LdomJ0MlsFek19mzc\n' +
  '3x5aEpMjXl10GgI0PDXxn522Vf8NAjAPNhCHZhHVys7Rxk0UDyOZJIApPaU+K58Y\n' +
  'T1Vu7ugEMi8xLKUOP4DhCS9u1E5rWul0crwjXXQOvwKBgQDqDBuwL0EJsS+U+pIL\n' +
  'v8X2VM/gCQyLraEaqoRdt1EdTX3dtJXuN31u6SJxp1I8Ldgojx9MN9RyMCIueXYK\n' +
  'srkmnXmqKRMCZ5yBqhNvgztRlhynlkuG3HzwEnn+H8YYXdoIFi1R24mFUH4ncgLn\n' +
  'fPrOUHPaEa46Xt6GJTHDs8cZ1wKBgQDG+UWkmNmr8PzE/cUN7XDQf/0wtkM5D4hd\n' +
  '101n9hXbsTbMTaNHM031jsTp0TmQprAlkJXyf9k0Qw4H54cG3Mp13sx6Ipc6KHSr\n' +
  'K6vZ2zVZmP48MO+kUK50sOYW+alVW7u7gxp/ikbFFxk71qYT7ocW6xvqLPbCfJix\n' +
  '8jVDPjBPbwKBgGmJbDLWHA2yyDH1i0WhBDM/qzzq161kbfEIUwevUfRUrRz8AV+Z\n' +
  'xgXtOorlWD+qrH4di49i3XlgGwm523CXhsXS3LnOK7Q55GjTuE/gbS8s8V7gds4p\n' +
  'Pm1C55+f1EPIjjh3R5hR1oE2+qvuzUB1KuM9hAAhWN8ZSF5VB0uoFW+hAoGAe0ad\n' +
  'm2kIE7tvIC2KFSLzLnrdx3xlEclJ17o5Pxv8Vuzo4pOHgemLd4RJBASZAcIEiIGY\n' +
  'JXKIhBS3cLZFtj69QWIUlecwgtvlzNd11bg2yZg8i9ZV4xwJukqPg9SS0UvQeFC1\n' +
  '7ichyaKSA7ztkJPMJqrr1J6fE8hWYomJ7mttSwkCgYAgcIkW7j8X7Rpuo8OH0f1V\n' +
  'pIe4SYY24wern9PtaAxK/v7eNumOBDszaPXNAC6EKcDjg+dTRJT/ack7P7+mXJu/\n' +
  'DSPbQVY25jJfwwfOCtoJiMyBA36Zos5UJsxQKhwW4KeXh+2+n3fDimOS47ER+IUG\n' +
  'Y0g2NdeezldNIekURddiGg==\n' +
  '-----END PRIVATE KEY-----';

let _cryptoKey = null;
let _certPem = null; // cached after first fetch to avoid repeat network calls on reconnect

function _pemToArrayBuffer(pem) {
  const b64 = pem
    .replace(/-----BEGIN PRIVATE KEY-----/, '')
    .replace(/-----END PRIVATE KEY-----/, '')
    .replace(/\s/g, '');
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

async function _getSigningKey() {
  if (_cryptoKey) return _cryptoKey;
  _cryptoKey = await crypto.subtle.importKey(
    'pkcs8',
    _pemToArrayBuffer(QZ_PRIVATE_KEY_PEM),
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-512' },
    false,
    ['sign']
  );
  return _cryptoKey;
}

// Certificate: fetched from the app's own public directory and cached in memory
// so subsequent connect() calls (e.g. auto-reconnect) skip the network round-trip.
qz.security.setCertificatePromise((resolve, reject) => {
  if (_certPem) {
    resolve(_certPem);
    return;
  }
  fetch(`${process.env.PUBLIC_URL || ''}/qz-certificate.pem`, { cache: 'no-store' })
    .then(resp => {
      if (!resp.ok) throw new Error('Certificate fetch failed: ' + resp.status);
      return resp.text();
    })
    .then(certPem => {
      console.log('[QZ Tray] Certificado cargado OK, longitud:', certPem.length);
      _certPem = certPem;
      resolve(certPem);
    })
    .catch(err => {
      console.error('[QZ Tray] Error cargando certificado:', err);
      reject(err);
    });
});

// Signature: sign each QZ Tray request using Web Crypto API (SHA-512 + RSA)
qz.security.setSignatureAlgorithm('SHA512');
qz.security.setSignaturePromise((toSign) => {
  return async (resolve, reject) => {
    try {
      const key = await _getSigningKey();
      const data = new TextEncoder().encode(toSign);
      const sig = await crypto.subtle.sign('RSASSA-PKCS1-v1_5', key, data);
      const b64 = btoa(String.fromCharCode(...new Uint8Array(sig)));
      resolve(b64);
    } catch (err) {
      console.error('[QZ Tray] Error al firmar:', err);
      reject(err);
    }
  };
});

let _reconnectTimer = null;
let _connectPromise = null;

// Callbacks invoked immediately when QZ Tray WebSocket closes unexpectedly.
// React context registers here to update UI state without waiting for the 15s timer.
const _desconexionCallbacks = [];

/**
 * Register a callback to be called when QZ Tray WebSocket closes.
 * @param {function} fn
 */
export function registrarCallbackDesconexion(fn) {
  if (typeof fn === 'function') _desconexionCallbacks.push(fn);
}

// Forward WebSocket close events to all registered listeners immediately.
qz.websocket.setClosedCallbacks((evt) => {
  console.warn('[QZ Tray] WebSocket cerrado inesperadamente:', evt?.reason || evt);
  _desconexionCallbacks.forEach(fn => { try { fn(); } catch (e) { console.error('[QZ Tray] Error en callback de desconexion:', e); } });
});

/**
 * Connect to QZ Tray via WebSocket.
 * If a connection attempt is already in progress (e.g. React StrictMode
 * double-mount), callers share the same promise instead of racing.
 * @param {Object} opts
 * @param {number} opts.retries - connection retries (default 0)
 * @returns {Promise<boolean>} true if connected
 */
export async function conectar({ retries = 0 } = {}) {
  try {
    // If a connect attempt is in flight, always wait for it regardless
    // of what isActive() says. isActive() returns true during CONNECTING
    // state but sendData isn't ready until the handshake fully completes.
    if (_connectPromise) {
      return await _connectPromise;
    }

    // Already fully connected from a previous call
    if (qz.websocket.isActive()) return true;

    _connectPromise = qz.websocket.connect({ retries })
      .then(() => true)
      .catch((err) => {
        console.warn('[QZ Tray] No se pudo conectar:', err?.message || err);
        return false;
      })
      .finally(() => {
        _connectPromise = null;
      });

    return await _connectPromise;
  } catch (err) {
    console.warn('[QZ Tray] No se pudo conectar:', err?.message || err);
    return false;
  }
}

/**
 * Disconnect from QZ Tray.
 * Waits for any in-flight connection attempt before disconnecting.
 */
export async function desconectar() {
  try {
    if (_reconnectTimer) {
      clearInterval(_reconnectTimer);
      _reconnectTimer = null;
    }
    if (_connectPromise) {
      await _connectPromise;
    }
    if (qz.websocket.isActive()) {
      await qz.websocket.disconnect();
    }
  } catch (err) {
    console.warn('[QZ Tray] Error al desconectar:', err?.message || err);
  }
}

/**
 * Whether QZ Tray WebSocket is active (fast, synchronous).
 */
export function estaConectado() {
  return qz.websocket.isActive();
}

/**
 * Verify the connection is truly alive by issuing a lightweight printer-list
 * request with a hard 3-second timeout.  Use this before critical print operations
 * to detect stale sockets (e.g. after system sleep/wake) where isActive() returns
 * true but the underlying TCP connection is dead.
 *
 * If the socket appears stale, this function disconnects it cleanly so the next
 * conectar() call opens a fresh WebSocket.
 *
 * @returns {Promise<boolean>} true = connection is healthy
 */
export async function verificarConexion() {
  if (!qz.websocket.isActive()) return false;
  try {
    await Promise.race([
      qz.printers.find(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('heartbeat timeout')), 3000)),
    ]);
    return true;
  } catch (err) {
    console.warn('[QZ Tray] Conexion estancada detectada:', err?.message, '— forzando reconexion');
    try { await qz.websocket.disconnect(); } catch { /* ignore */ }
    _connectPromise = null;
    return false;
  }
}

/**
 * Start auto-reconnect loop.
 * @param {function} onReconexion - called when reconnection succeeds
 * @param {Object} opts
 * @param {function} opts.onDesconexion - called when a disconnect is detected (before reconnect attempt)
 * @param {number}   opts.intervalMs   - poll interval in ms (default 15 000)
 */
export function iniciarAutoReconexion(onReconexion, { onDesconexion, intervalMs = 15000 } = {}) {
  if (_reconnectTimer) return;
  _reconnectTimer = setInterval(async () => {
    if (!qz.websocket.isActive()) {
      if (typeof onDesconexion === 'function') onDesconexion();
      const ok = await conectar({ retries: 0 });
      if (ok && typeof onReconexion === 'function') onReconexion();
    } else {
      // Background stale-socket detection (e.g. after system sleep/wake).
      // Runs here so it never blocks the print hot-path.
      const viva = await verificarConexion();
      if (!viva) {
        if (typeof onDesconexion === 'function') onDesconexion();
        const ok = await conectar({ retries: 0 });
        if (ok && typeof onReconexion === 'function') onReconexion();
      }
    }
  }, intervalMs);
}

/**
 * Get list of available printer names.
 * @returns {Promise<string[]>}
 */
export async function obtenerImpresoras() {
  try {
    if (!qz.websocket.isActive()) return [];
    const printers = await qz.printers.find();
    return Array.isArray(printers) ? printers : [printers];
  } catch (err) {
    console.error('[QZ Tray] Error al listar impresoras:', err?.message || err);
    return [];
  }
}

/**
 * Send raw ESC/POS bytes to a specific printer.
 * Attempts reconnection if the WebSocket is down, then retries once on transient
 * print failures (printer busy, USB hiccup, brief WebSocket blip).
 *
 * @param {string} base64Bytes   - base64-encoded ESC/POS data
 * @param {string} nombreImpresora - printer name
 * @param {Object} opts
 * @param {number} opts.maxReintentos - max retry count after first failure (default 1)
 * @returns {Promise<boolean>} success
 */
export async function imprimirRaw(base64Bytes, nombreImpresora, { maxReintentos = 1 } = {}) {
  for (let intento = 0; intento <= maxReintentos; intento++) {
    try {
      if (!qz.websocket.isActive()) {
        console.warn(`[QZ Tray] imprimirRaw: WebSocket inactivo, intentando reconectar (intento ${intento + 1})`);
        const reconectado = await conectar({ retries: 0 });
        if (!reconectado) {
          if (intento < maxReintentos) await new Promise(r => setTimeout(r, 500));
          continue;
        }
      }
      const config = qz.configs.create(nombreImpresora);
      const data = [{ type: 'raw', format: 'base64', data: base64Bytes }];
      await qz.print(config, data);
      return true;
    } catch (err) {
      console.warn(`[QZ Tray] imprimirRaw intento ${intento + 1} fallido:`, err?.message || err);
      if (intento < maxReintentos) await new Promise(r => setTimeout(r, 500));
    }
  }
  console.error('[QZ Tray] imprimirRaw: todos los intentos fallaron para impresora:', nombreImpresora);
  return false;
}

/**
 * Send a PDF to a specific printer via QZ Tray's PDF rendering.
 * Attempts reconnection if the WebSocket is down, then retries once on transient failures.
 *
 * @param {string} base64PDF     - base64-encoded PDF
 * @param {string} nombreImpresora - printer name
 * @param {Object} opts
 * @param {number} opts.maxReintentos - max retry count after first failure (default 1)
 * @returns {Promise<boolean>} success
 */
export async function imprimirPDF(base64PDF, nombreImpresora, { maxReintentos = 1 } = {}) {
  for (let intento = 0; intento <= maxReintentos; intento++) {
    try {
      if (!qz.websocket.isActive()) {
        console.warn(`[QZ Tray] imprimirPDF: WebSocket inactivo, intentando reconectar (intento ${intento + 1})`);
        const reconectado = await conectar({ retries: 0 });
        if (!reconectado) {
          if (intento < maxReintentos) await new Promise(r => setTimeout(r, 500));
          continue;
        }
      }
      const config = qz.configs.create(nombreImpresora, {
        size: { width: 3.15 }, // 80mm ≈ 3.15 inches
        margins: { top: 0, right: 0, bottom: 0, left: 0 },
        units: 'in'
      });
      const data = [{ type: 'pixel', format: 'base64', flavor: 'plain', data: base64PDF }];
      await qz.print(config, data);
      return true;
    } catch (err) {
      console.warn(`[QZ Tray] imprimirPDF intento ${intento + 1} fallido:`, err?.message || err);
      if (intento < maxReintentos) await new Promise(r => setTimeout(r, 500));
    }
  }
  console.error('[QZ Tray] imprimirPDF: todos los intentos fallaron para impresora:', nombreImpresora);
  return false;
}

/**
 * Send raw plain-text data (e.g. ZPL commands) to a specific printer.
 * Attempts reconnection if the WebSocket is down, then retries once on transient failures.
 *
 * @param {string} plainText       - plain-text data to send (e.g. ZPL string)
 * @param {string} nombreImpresora - printer name
 * @param {Object} opts
 * @param {number} opts.maxReintentos - max retry count after first failure (default 1)
 * @returns {Promise<boolean>} success
 */
export async function imprimirRawPlain(plainText, nombreImpresora, { maxReintentos = 1 } = {}) {
  for (let intento = 0; intento <= maxReintentos; intento++) {
    try {
      if (!qz.websocket.isActive()) {
        console.warn(`[QZ Tray] imprimirRawPlain: WebSocket inactivo, intentando reconectar (intento ${intento + 1})`);
        const reconectado = await conectar({ retries: 0 });
        if (!reconectado) {
          if (intento < maxReintentos) await new Promise(r => setTimeout(r, 500));
          continue;
        }
      }
      const config = qz.configs.create(nombreImpresora);
      const data = [{ type: 'raw', format: 'plain', data: plainText }];
      await qz.print(config, data);
      return true;
    } catch (err) {
      console.warn(`[QZ Tray] imprimirRawPlain intento ${intento + 1} fallido:`, err?.message || err);
      if (intento < maxReintentos) await new Promise(r => setTimeout(r, 500));
    }
  }
  console.error('[QZ Tray] imprimirRawPlain: todos los intentos fallaron para impresora:', nombreImpresora);
  return false;
}

/**
 * Send a test print to verify printer works.
 * @param {string} nombreImpresora
 * @returns {Promise<boolean>}
 */
export async function imprimirPrueba(nombreImpresora) {
  try {
    if (!qz.websocket.isActive()) return false;
    const ESC = '\x1B';
    const GS = '\x1D';
    const testData = [
      `${ESC}@`,          // init
      `${ESC}a\x01`,      // center
      `${ESC}E\x01`,      // bold on
      'PRUEBA DE IMPRESION\n',
      `${ESC}E\x00`,      // bold off
      '================================\n',
      `Impresora: ${nombreImpresora}\n`,
      `Fecha: ${new Date().toLocaleString()}\n`,
      '================================\n',
      'Si puede leer este texto, la\n',
      'impresora esta configurada\n',
      'correctamente con QZ Tray.\n',
      '================================\n',
      '\n\n\n',
      `${GS}V\x42\x03`,   // partial cut
    ].join('');

    const config = qz.configs.create(nombreImpresora);
    const data = [{ type: 'raw', format: 'plain', data: testData }];
    await qz.print(config, data);
    return true;
  } catch (err) {
    console.error('[QZ Tray] Error en prueba de impresion:', err?.message || err);
    return false;
  }
}

const qzTrayService = {
  conectar,
  desconectar,
  estaConectado,
  verificarConexion,
  iniciarAutoReconexion,
  registrarCallbackDesconexion,
  obtenerImpresoras,
  imprimirRaw,
  imprimirRawPlain,
  imprimirPDF,
  imprimirPrueba
};

export default qzTrayService;
